"use client";
import React, { useState, useRef } from "react";

export const CommentForm = ({ onSubmit }) => {
  const [comment, setComment] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleCommentChange = (e) => {
    setComment(e.target.value);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setAttachment(null);
      setPreview(null);
      return;
    }

    setAttachment({
      file,
      name: file.name,
      type: file.type.startsWith("image/")
        ? "image"
        : file.type.startsWith("video/")
          ? "video"
          : "file",
    });

    // Create preview for images
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!comment.trim()) return;

    // In a real app, you would upload the file to a server and get a URL back
    // For this example, we'll simulate that by creating an object URL
    let attachmentData = null;
    if (attachment) {
      attachmentData = {
        url: preview || URL.createObjectURL(attachment.file),
        name: attachment.name,
        type: attachment.type,
      };
    }

    onSubmit({
      content: comment,
      attachment: attachmentData,
      date: new Date().toISOString(),
    });

    // Reset form
    setComment("");
    setAttachment(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4">
      <div className="mb-3">
        <textarea
          className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
          rows="3"
          placeholder="Add a comment..."
          value={comment}
          onChange={handleCommentChange}
          required
        ></textarea>
      </div>

      {preview && (
        <div className="relative mb-3 inline-block">
          <img
            src={preview}
            alt="Preview"
            className="h-20 rounded-lg border border-gray-200"
          />
          <button
            type="button"
            onClick={removeAttachment}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
          >
            ×
          </button>
        </div>
      )}

      {attachment && !preview && (
        <div className="flex items-center gap-2 mb-3 text-sm">
          <span className="text-gray-700">{attachment.name}</span>
          <button
            type="button"
            onClick={removeAttachment}
            className="text-red-500 hover:text-red-700"
          >
            Remove
          </button>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            id="attachment"
            accept="image/*,video/*,.pdf,.doc,.docx,.txt"
          />
          <label
            htmlFor="attachment"
            className="cursor-pointer flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
              />
            </svg>
            <span className="text-sm">Add attachment</span>
          </label>
        </div>

        <button
          type="submit"
          className="px-4 py-2 bg-rose-700 text-white text-sm font-medium rounded-lg hover:bg-rose-800 focus:outline-none focus:ring-2 focus:ring-rose-500"
          disabled={!comment.trim()}
        >
          Comment
        </button>
      </div>
    </form>
  );
};
